// Sample-level edits used by the audio editor. They work on plain
// Float32Array channels (not AudioBuffers), always use whole-sample indices,
// and never modify their input, so they can be unit-tested and are safe to
// use with the editor's undo history.

export type Channels = Float32Array[];

// Short fade applied where audio is cut, so a cut doesn't "click".
export const EDGE_FADE_MS = 5;

const msToSamples = (ms: number, sampleRate: number) => Math.round((ms / 1000) * sampleRate);

export const channelLength = (channels: Channels) => channels[0]?.length ?? 0;

export const copyChannels = (channels: Channels): Channels => channels.map((c) => c.slice());

// Seconds → sample index, rounded and kept inside [0, length].
export const secondsToSample = (seconds: number, sampleRate: number, length: number) =>
  Math.min(length, Math.max(0, Math.round((Number.isFinite(seconds) ? seconds : 0) * sampleRate)));

const fadeInPlace = (data: Float32Array, samples: number, direction: "in" | "out") => {
  const n = Math.min(samples, data.length);
  if (n <= 0) return;
  for (let i = 0; i < n; i++) {
    const gain = i / n;
    if (direction === "in") data[i] *= gain;
    else data[data.length - 1 - i] *= gain;
  }
};

// Keep only [start, end). Edges that are real cuts (not the original start
// or end of the audio) get a short fade.
export const keepRange = (
  channels: Channels,
  sampleRate: number,
  startSec: number,
  endSec: number,
  edgeFadeMs = EDGE_FADE_MS
): Channels => {
  const length = channelLength(channels);
  const start = secondsToSample(Math.min(startSec, endSec), sampleRate, length);
  const end = secondsToSample(Math.max(startSec, endSec), sampleRate, length);
  if (end - start < 1) return copyChannels(channels);
  const fade = Math.min(msToSamples(edgeFadeMs, sampleRate), Math.floor((end - start) / 2));
  return channels.map((ch) => {
    const out = ch.slice(start, end);
    if (start > 0) fadeInPlace(out, fade, "in");
    if (end < length) fadeInPlace(out, fade, "out");
    return out;
  });
};

// Cut [start, end) out and join what's left, with a short fade on both sides
// of the join.
export const deleteRange = (
  channels: Channels,
  sampleRate: number,
  startSec: number,
  endSec: number,
  edgeFadeMs = EDGE_FADE_MS
): Channels => {
  const length = channelLength(channels);
  const start = secondsToSample(Math.min(startSec, endSec), sampleRate, length);
  const end = secondsToSample(Math.max(startSec, endSec), sampleRate, length);
  if (end - start < 1) return copyChannels(channels);
  // Deleting everything would leave an empty buffer, which AudioBuffer
  // can't hold; keep a single silent sample instead.
  if (start === 0 && end === length) return channels.map(() => new Float32Array(1));
  const fadeMax = msToSamples(edgeFadeMs, sampleRate);
  return channels.map((ch) => {
    const before = ch.slice(0, start);
    const after = ch.slice(end);
    if (before.length && after.length) {
      fadeInPlace(before, Math.min(fadeMax, before.length), "out");
      fadeInPlace(after, Math.min(fadeMax, after.length), "in");
    }
    const out = new Float32Array(before.length + after.length);
    out.set(before, 0);
    out.set(after, before.length);
    return out;
  });
};

// Move the audio later (positive ms: silence is added at the start) or
// earlier (negative ms: the start is cut off, with a short fade-in).
export const shiftAudio = (channels: Channels, sampleRate: number, offsetMs: number, edgeFadeMs = EDGE_FADE_MS): Channels => {
  const length = channelLength(channels);
  const samples = msToSamples(offsetMs, sampleRate);
  if (samples === 0 || length === 0) return copyChannels(channels);
  if (samples > 0) {
    return channels.map((ch) => {
      const out = new Float32Array(length + samples);
      out.set(ch, samples);
      return out;
    });
  }
  const drop = Math.min(-samples, length - 1);
  return channels.map((ch) => {
    const out = ch.slice(drop);
    fadeInPlace(out, Math.min(msToSamples(edgeFadeMs, sampleRate), out.length), "in");
    return out;
  });
};

// Change playback speed by resampling with linear interpolation. Like the
// browser's own playback rate, this changes pitch as well as length.
// (The previous version skipped or repeated whole samples, which sounded
// rough, and for speeds like 0.75× didn't slow the audio down at all — it
// only appended silence.)
export const resampleForSpeed = (channels: Channels, speed: number): Channels => {
  const length = channelLength(channels);
  if (!Number.isFinite(speed) || speed <= 0 || speed === 1 || length === 0) return copyChannels(channels);
  const outLength = Math.max(1, Math.round(length / speed));
  return channels.map((ch) => {
    const out = new Float32Array(outLength);
    for (let i = 0; i < outLength; i++) {
      const pos = i * speed;
      const i0 = Math.min(Math.floor(pos), length - 1);
      const i1 = Math.min(i0 + 1, length - 1);
      const frac = pos - Math.floor(pos);
      out[i] = ch[i0] * (1 - frac) + ch[i1] * frac;
    }
    return out;
  });
};

export const peakOf = (channels: Channels) => {
  let peak = 0;
  for (const ch of channels) {
    for (let i = 0; i < ch.length; i++) {
      const v = Math.abs(ch[i]);
      if (v > peak) peak = v;
    }
  }
  return peak;
};

export const scaleChannels = (channels: Channels, gain: number): Channels =>
  channels.map((ch) => {
    const out = new Float32Array(ch.length);
    for (let i = 0; i < ch.length; i++) out[i] = ch[i] * gain;
    return out;
  });

export const dbToGain = (db: number) => Math.pow(10, db / 20);

// −1 dB leaves a little headroom so the audio doesn't distort when it's
// encoded or mixed with the main track.
export const NORMALIZE_TARGET_DB = -1;

export const normalizePeak = (channels: Channels, targetDb = NORMALIZE_TARGET_DB): Channels | null => {
  const peak = peakOf(channels);
  if (peak === 0) return null;
  return scaleChannels(channels, dbToGain(targetDb) / peak);
};

// Scale down only if the audio would clip.
export const preventClipping = (channels: Channels, ceiling = 0.98): Channels => {
  const peak = peakOf(channels);
  return peak > ceiling ? scaleChannels(channels, ceiling / peak) : channels;
};

// Waveform overview for the editor's "Main track" lane: the loudest sample
// (across channels) in each 1/peaksPerSecond slice, scaled so the loudest
// slice is 1. Small enough to redraw on every scroll or zoom.
export const computePeaks = (channels: Channels, sampleRate: number, peaksPerSecond = 200): Float32Array => {
  const length = channelLength(channels);
  const size = Math.max(1, Math.ceil((length / sampleRate) * peaksPerSecond));
  const peaks = new Float32Array(size);
  const samplesPerPeak = sampleRate / peaksPerSecond;
  for (const ch of channels) {
    for (let i = 0; i < ch.length; i++) {
      const p = Math.min(size - 1, Math.floor(i / samplesPerPeak));
      const v = Math.abs(ch[i]);
      if (v > peaks[p]) peaks[p] = v;
    }
  }
  let max = 0;
  for (let i = 0; i < size; i++) if (peaks[i] > max) max = peaks[i];
  if (max > 0) for (let i = 0; i < size; i++) peaks[i] /= max;
  return peaks;
};

export interface GateSettings {
  thresholdDb: number; // below this level the audio is silenced
  attackMs?: number;
  holdMs?: number;
  releaseMs?: number;
}

// Noise gate: silences the quiet background between phrases. The gain is
// shared by all channels and changes smoothly, so the gate doesn't click.
export const noiseGate = (channels: Channels, sampleRate: number, settings: GateSettings): Channels => {
  const length = channelLength(channels);
  if (length === 0) return copyChannels(channels);
  const threshold = dbToGain(settings.thresholdDb);
  const attack = Math.max(1, msToSamples(settings.attackMs ?? 5, sampleRate));
  const release = Math.max(1, msToSamples(settings.releaseMs ?? 120, sampleRate));
  const hold = msToSamples(settings.holdMs ?? 60, sampleRate);
  // Level follower: rises instantly, falls over ~10 ms.
  const envFall = Math.exp(-1 / Math.max(1, msToSamples(10, sampleRate)));

  const out = channels.map((ch) => new Float32Array(ch.length));
  let env = 0;
  let gain = 0;
  let holdLeft = 0;
  for (let i = 0; i < length; i++) {
    let level = 0;
    for (const ch of channels) {
      const v = Math.abs(ch[i]);
      if (v > level) level = v;
    }
    env = level > env ? level : env * envFall;
    if (env >= threshold) holdLeft = hold;
    const open = env >= threshold || holdLeft > 0;
    if (holdLeft > 0) holdLeft--;
    gain = open ? Math.min(1, gain + 1 / attack) : Math.max(0, gain - 1 / release);
    for (let c = 0; c < channels.length; c++) out[c][i] = channels[c][i] * gain;
  }
  return out;
};
